import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import TaskTemplateEditor from '../../components/coachLed/TaskTemplateEditor.jsx';
import CurriculumPreview from '../../components/coachLed/CurriculumPreview.jsx';
import ProgramResourcesUpload from '../../components/coachLed/ProgramResourcesUpload.jsx';

const GOAL_OPTIONS = [
  { value: 'grow', label: 'Grow', description: 'Increase business value' },
  { value: 'exit', label: 'Exit', description: 'Prepare for exit' },
  { value: 'protect', label: 'Protect', description: 'Risk mitigation' },
];

export default function CoachProgramEditorContainer(props) {
  const programId = props?.program_id || 'new';
  const isNew = programId === 'new';

  // Load existing program if editing
  const PROGRAM_QUERY = gql`
    query ProgramForEdit($id: ID) {
      sprintProgram(id: $id) {
        id
        title
        summary
        duration_days
        level
        price_cents
        is_group
        max_participants
        goals
        benefits
        status
        enrollment_count
        documents {
          id
          name
          download_url
          created_at
          can_delete
          uploaded_by {
            name
          }
        }
        taskTemplates {
          id
          title
          description_md
          estimated_minutes
          requires_attachment
          day_offset
          canonical_task_type
          educationalContent {
            id
            title
            description
            locale
            content_type
            asset_url
            embed_code
            mdBody
            sortOrder
          }
        }
      }
    }
  `;
  const { data, refetch: refetchProgram } = useQuery(PROGRAM_QUERY, {
    variables: { id: isNew ? null : programId },
    skip: isNew,
  });

  const CREATE_MUTATION = gql`
    mutation CreateProgram(
      $coach_profile_id: ID!
      $title: String!
      $slug: String!
      $summary: String
      $benefits: [String!]
      $duration_days: Int!
      $level: String!
      $price_cents: Int
      $is_group: Boolean
      $max_participants: Int
      $goals: [String!]
      $hero_image_url: String
    ) {
      create_sprint_program(
        coach_profile_id: $coach_profile_id
        title: $title
        slug: $slug
        summary: $summary
        benefits: $benefits
        duration_days: $duration_days
        level: $level
        price_cents: $price_cents
        is_group: $is_group
        max_participants: $max_participants
        goals: $goals
        hero_image_url: $hero_image_url
      ) {
        id
        slug
      }
    }
  `;

  const UPDATE_MUTATION = gql`
    mutation UpdateProgram(
      $id: ID!
      $title: String
      $summary: String
      $benefits: [String!]
      $duration_days: Int
      $level: String
      $price_cents: Int
      $is_group: Boolean
      $max_participants: Int
      $goals: [String!]
      $hero_image_url: String
    ) {
      update_sprint_program(
        id: $id
        title: $title
        summary: $summary
        benefits: $benefits
        duration_days: $duration_days
        level: $level
        price_cents: $price_cents
        is_group: $is_group
        max_participants: $max_participants
        goals: $goals
        hero_image_url: $hero_image_url
      ) {
        id
        slug
      }
    }
  `;

  const UPSERT_TEMPLATES_MUTATION = gql`
    mutation UpsertTemplates($sprint_program_id: ID!, $templates: [SprintProgramTaskTemplateInput!]!) {
      upsert_sprint_program_task_templates(sprint_program_id: $sprint_program_id, templates: $templates) {
        task_templates {
          id
        }
      }
    }
  `;

  const [createProgram] = useMutation(CREATE_MUTATION);
  const [updateProgram] = useMutation(UPDATE_MUTATION);
  const [upsertTemplates] = useMutation(UPSERT_TEMPLATES_MUTATION);

  const SUBMIT_FOR_REVIEW_MUTATION = gql`
    mutation SubmitForReview($id: ID!) {
      submit_sprint_program_for_review(id: $id) {
        id
        status
      }
    }
  `;
  const [submitForReview] = useMutation(SUBMIT_FOR_REVIEW_MUTATION);
  // Form state (client-side draft)
  const [form, setForm] = useState({
    title: '',
    summary: '',
    duration_days: 30,
    level: 'starter',
    goals: [],
    benefits: ['', '', '', ''],
    price_cents: null,
    is_group: false,
    max_participants: '',
  });

  const [templates, setTemplates] = useState([
    {
      id: 'tmp-1',
      title: 'Initial Assessment',
      description_md:
        'Complete the initial assessment to understand your current state.',
      estimated_minutes: 60,
      requires_attachment: false,
      day_offset: 0,
      canonical_task_type: 'custom',
    },
  ]);

  const [tab, setTab] = useState('basics'); // basics | curriculum | pricing | resources
  const [showPreview, setShowPreview] = useState(false);
  const [priceInput, setPriceInput] = useState('');

  // Initialize from loaded data once
  useEffect(() => {
    const p = data?.sprintProgram;
    if (!isNew && p) {
      setForm((f) => ({
        ...f,
        title: p.title || '',
        summary: p.summary || '',
        duration_days: p.duration_days || 30,
        level: p.level || 'starter',
        goals: Array.isArray(p.goals) ? p.goals : [],
        benefits: Array.isArray(p.benefits) && p.benefits.length ? p.benefits : ['', '', '', ''],
        price_cents: p.price_cents,
        is_group: !!p.is_group,
        max_participants: p.max_participants || '',
      }));
      setPriceInput(p.price_cents != null ? String(p.price_cents / 100) : '');
      setTemplates(
        (p.taskTemplates || []).map((t) => ({
          id: t.id,
          title: t.title,
          description_md: t.description_md,
          estimated_minutes: t.estimated_minutes,
          requires_attachment: t.requires_attachment,
          day_offset: t.day_offset,
          canonical_task_type: t.canonical_task_type,
          educationalContent: (t.educationalContent || []).map((ec) => {
            // Extract documentId from asset_url if it contains ?doc_id=
            let documentId = null;
            let fileName = '';
            if (ec.asset_url && ec.asset_url.includes('?doc_id=')) {
              const match = ec.asset_url.match(/[?&]doc_id=(\d+)/);
              if (match) {
                documentId = match[1];
              }
              // Extract filename from URL if possible
              const urlMatch = ec.asset_url.match(/\/([^/?]+)(?:\?|$)/);
              if (urlMatch) {
                fileName = decodeURIComponent(urlMatch[1]);
              }
            }
            return {
              id: ec.id,
              title: ec.title,
              description: ec.description,
              locale: ec.locale,
              content_type: ec.content_type,
              content: ec.mdBody || ec.embed_code || '',
              url: ec.asset_url || '',
              fileName: fileName,
              documentId: documentId,
              sortOrder: ec.sortOrder || 0,
            };
          }),
        }))
      );
    }
  }, [data, isNew]);

  const onChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const toggleGoal = (goal) =>
    setForm((f) => ({
      ...f,
      goals: f.goals.includes(goal)
        ? f.goals.filter((g) => g !== goal)
        : [...f.goals, goal],
    }));
  const addBenefit = () =>
    setForm((f) => ({ ...f, benefits: [...f.benefits, ''] }));
  const setBenefit = (i, value) =>
    setForm((f) => {
      const next = [...f.benefits];
      next[i] = value;
      return { ...f, benefits: next };
    });

  const totalMinutes = useMemo(
    () =>
      (templates || []).reduce(
        (sum, t) => sum + (parseInt(t.estimated_minutes, 10) || 0),
        0
      ),
    [templates]
  );

  // Shared function to save templates
  const doUpsertTemplates = async (id) => {
    // Ensure we have all templates - defensive check
    // If editing existing program and we have server data, merge with current state
    let allTemplates = templates || [];
    
    if (!isNew && data?.sprintProgram?.taskTemplates) {
      const serverTemplates = data.sprintProgram.taskTemplates || [];
      const serverTemplateIds = new Set(serverTemplates.map(t => t.id));
      const currentTemplateIds = new Set(allTemplates.map(t => t.id));
      
      // If we're missing templates from server, merge them in
      // This can happen if TaskTemplateEditor local state wasn't synced properly
      if (serverTemplates.length > allTemplates.length) {
        console.warn(`Missing templates in state! Server has ${serverTemplates.length}, state has ${allTemplates.length}. Merging...`);
        
        // Create a map of current templates by ID for quick lookup
        const currentTemplatesMap = new Map(allTemplates.map(t => [t.id, t]));
        
        // Merge: use current template if it exists (has edits), otherwise use server template
        allTemplates = serverTemplates.map(serverT => {
          const currentT = currentTemplatesMap.get(serverT.id);
          if (currentT) {
            // Use current template (has user edits)
            return currentT;
          } else {
            // Use server template (wasn't in local state)
            return {
              id: serverT.id,
              title: serverT.title,
              description_md: serverT.description_md,
              estimated_minutes: serverT.estimated_minutes,
              requires_attachment: serverT.requires_attachment,
              day_offset: serverT.day_offset,
              canonical_task_type: serverT.canonical_task_type,
              educationalContent: (serverT.educationalContent || []).map((ec) => ({
                id: ec.id,
                title: ec.title,
                description: ec.description,
                locale: ec.locale,
                content_type: ec.content_type,
                content: ec.mdBody || ec.embed_code || '',
                url: ec.asset_url || '',
                fileName: ec.fileName || '',
                documentId: ec.documentId || null,
                sortOrder: ec.sortOrder || 0,
              })),
            };
          }
        });
      }
    }
    
    if (allTemplates.length === 0) {
      console.warn('No templates to save');
      return;
    }

    // Log for debugging - remove in production if needed
    console.log(`Saving ${allTemplates.length} templates for program ${id}`);
    console.log('Template IDs:', allTemplates.map(t => t.id));
    console.log('Template titles:', allTemplates.map(t => t.title));

    const payload = allTemplates.map((t) => ({
      // Note: input type doesn't accept id; server will replace entire set
      title: t.title,
      description_md: t.description_md,
      estimated_minutes: parseInt(t.estimated_minutes, 10) || 0,
      requires_attachment: !!t.requires_attachment,
      day_offset: parseInt(t.day_offset, 10) || 0,
      canonical_task_type: t.canonical_task_type || 'custom',
      educational_content: (t.educationalContent || []).map((ec) => ({
        id: ec.id,
        title: ec.title,
        description: ec.description || '',
        locale: ec.locale || 'EN',
        content_type: ec.content_type,
        content: ec.content || '',
        url: ec.url || '',
        file_name: ec.fileName || '',
        document_id: ec.documentId || null,
        sort_order: ec.sortOrder || 0,
      })),
    }));
    try {
      await upsertTemplates({
        variables: { sprint_program_id: id, templates: payload },
      });
      // Refetch program data to ensure UI is in sync with server
      if (!isNew && refetchProgram) {
        await refetchProgram();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to save templates:', e);
      throw e; // Re-throw so caller can handle
    }
  };

  const handleSaveDraft = () => {
    const slugify = (s) =>
      (s || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '') || `program-${Date.now()}`;

    (async () => {
      try {
        if (window.ahoy && typeof window.ahoy.track === 'function') {
          window.ahoy.track('coach_program_save_draft', { program_id: programId, tab });
        }
      } catch (_) {}

      try {
        if (isNew) {
          // TODO: swap with real coach profile id when wired; for now read from props if present
          const coachProfileId = props?.coach_profile_id || props?.coachProfileId || props?.coach_id || '1';
          const slug = slugify(form.title);
          const res = await createProgram({
            variables: {
              coach_profile_id: coachProfileId,
              title: form.title || 'Untitled Program',
              slug,
              summary: form.summary || '',
              benefits: form.benefits,
              duration_days: parseInt(form.duration_days, 10) || 30,
              level: form.level || 'starter',
              price_cents: form.price_cents,
              is_group: !!form.is_group,
              max_participants:
                form.max_participants !== '' ? parseInt(form.max_participants, 10) : null,
              goals: form.goals || [],
              hero_image_url: null,
            },
          });
          const newId = res?.data?.create_sprint_program?.id;
          if (newId) {
            await doUpsertTemplates(newId);
            window.location.assign(`/coach/programs/${newId}/edit`);
            return;
          }
        } else {
          const res = await updateProgram({
            variables: {
              id: programId,
              title: form.title || undefined,
              summary: form.summary || undefined,
              benefits: form.benefits || undefined,
              duration_days: parseInt(form.duration_days, 10) || undefined,
              level: form.level || undefined,
              price_cents: form.price_cents != null ? form.price_cents : undefined,
              is_group: form.is_group,
              max_participants:
                form.max_participants !== '' ? parseInt(form.max_participants, 10) : undefined,
              goals: form.goals || undefined,
              hero_image_url: undefined,
            },
          });
          const existingId = res?.data?.update_sprint_program?.id || programId;
          await doUpsertTemplates(existingId);
          alert('Draft saved.');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        alert('Failed to save draft. Please check required fields and try again.');
      }
    })();
  };

  const handleSubmitForReview = () => {
    const slugify = (s) =>
      (s || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '') || `program-${Date.now()}`;

    (async () => {
      try {
        if (window.ahoy && typeof window.ahoy.track === 'function') {
          window.ahoy.track('coach_program_submit_review', { program_id: programId });
        }
      } catch (_) {}

      try {
        // Ensure the latest edits are saved first
        let targetId = programId;
        if (isNew) {
          const coachProfileId = props?.coach_profile_id || props?.coachProfileId || props?.coach_id || '1';
          const slug = slugify(form.title);
          const resCreate = await createProgram({
            variables: {
              coach_profile_id: coachProfileId,
              title: form.title || 'Untitled Program',
              slug,
              summary: form.summary || '',
              benefits: form.benefits,
              duration_days: parseInt(form.duration_days, 10) || 30,
              level: form.level || 'starter',
              price_cents: form.price_cents,
              is_group: !!form.is_group,
              max_participants:
                form.max_participants !== '' ? parseInt(form.max_participants, 10) : null,
              goals: form.goals || [],
              hero_image_url: null,
            },
          });
          targetId = resCreate?.data?.create_sprint_program?.id;
          if (!targetId) throw new Error('Failed to create program.');
        } else {
          await updateProgram({
            variables: {
              id: targetId,
              title: form.title || undefined,
              summary: form.summary || undefined,
              benefits: form.benefits || undefined,
              duration_days: parseInt(form.duration_days, 10) || undefined,
              level: form.level || undefined,
              price_cents: form.price_cents != null ? form.price_cents : undefined,
              is_group: form.is_group,
              max_participants:
                form.max_participants !== '' ? parseInt(form.max_participants, 10) : undefined,
              goals: form.goals || undefined,
              hero_image_url: undefined,
            },
          });
        }

        // Upsert templates before submitting
        await doUpsertTemplates(targetId);

        // Submit for review (status -> review)
        await submitForReview({ variables: { id: targetId } });
        alert('Submitted for review. An administrator will review your program.');
        window.location.assign('/coach/portal');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        alert('Failed to submit for review. Please check required fields and try again.');
      }
    })();
  };

  const priceDisplay =
    form.price_cents != null
      ? `$${(form.price_cents / 100).toLocaleString()}`
      : 'Free';

  // Get program title from data or form state
  const programTitle = !isNew && data?.sprintProgram?.title 
    ? data.sprintProgram.title 
    : form.title || '';

  return (
    <SingleColumnTemplate
      title={isNew ? 'Create Program' : 'Edit Program'}
      subtitle={isNew ? 'Build a new sprint program' : `Editing: ${programTitle || 'Loading...'}`}
      variant="wide"
    >
      {/* Header actions */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          className="rounded-lg border border-border bg-card px-3 py-2 hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
          onClick={() => setShowPreview((v) => !v)}
        >
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-3 py-2 hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
          onClick={handleSaveDraft}
        >
          Save Draft
        </button>
        <button
          className="rounded-lg bg-primary text-primary-foreground px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
          onClick={handleSubmitForReview}
        >
          Submit for Review
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Editor */}
        <div className={showPreview ? 'lg:col-span-2' : 'lg:col-span-3'}>
          {/* Tabs */}
          <div className="rounded-2xl border border-border bg-card p-3 mb-3">
            <div className="flex items-center gap-2">
              {[
                { id: 'basics', label: 'Basics' },
                { id: 'curriculum', label: 'Curriculum' },
                { id: 'pricing', label: 'Pricing & Access' },
                { id: 'resources', label: 'Resources' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] hover:bg-[var(--succession-hover-bg)] ${
                    tab === t.id ? 'bg-card border-border shadow-sm' : 'border-transparent bg-muted'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Basics */}
          {tab === 'basics' && (
            <div className="rounded-2xl border border-border bg-card p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Program Title *</label>
                <input
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  value={form.title}
                  onChange={(e) => onChange('title', e.target.value)}
                  placeholder="e.g., Valuation Tune-Up: Know Your True Worth"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Summary *</label>
                <textarea
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                  value={form.summary}
                  onChange={(e) => onChange('summary', e.target.value)}
                  placeholder="Brief description of what participants will achieve"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Duration (days) *</label>
                  <input
                    type="number"
                    min={1}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                    value={form.duration_days}
                    onChange={(e) =>
                      onChange('duration_days', parseInt(e.target.value, 10) || 0)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Level *</label>
                  <select
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                    value={form.level}
                    onChange={(e) => onChange('level', e.target.value)}
                  >
                    <option value="starter">Starter</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-medium">Program Goals *</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {GOAL_OPTIONS.map((g) => {
                    const selected = form.goals.includes(g.value);
                    return (
                      <button
                        key={g.value}
                        type="button"
                        onClick={() => toggleGoal(g.value)}
                        className={`text-left rounded-lg border px-4 py-3 transition-all focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] ${
                          selected ? 'ring-2 ring-primary bg-primary/5' : 'border-border hover:bg-[var(--succession-hover-bg)]'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className={`w-5 h-5 rounded border-2 mt-0.5 ${
                              selected ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}
                          />
                          <div>
                            <div className="font-medium">{g.label}</div>
                            <div className="text-xs text-muted-foreground">{g.description}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Key Benefits</label>
                  <button
                    className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                    onClick={addBenefit}
                  >
                    Add Benefit
                  </button>
                </div>
                <div className="space-y-2">
                  {form.benefits.map((b, i) => (
                    <input
                      key={i}
                      className="w-full rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                      value={b}
                      placeholder={`Benefit ${i + 1}`}
                      onChange={(e) => setBenefit(i, e.target.value)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Curriculum */}
          {tab === 'curriculum' && (
            <div className="rounded-2xl border border-border bg-card p-6">
              <TaskTemplateEditor templates={templates} onChange={setTemplates} />
              <div className="mt-4 rounded-xl border border-border bg-card p-3 text-sm text-muted-foreground">
                {templates.length} task{templates.length !== 1 ? 's' : ''} •{' '}
                {Math.round(totalMinutes / 60)} hours total
              </div>
            </div>
          )}

          {/* Pricing & Access */}
          {tab === 'pricing' && (
            <div className="rounded-2xl border border-border bg-card p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Price (USD)</label>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-48 rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                    placeholder="0.00"
                    value={priceInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setPriceInput(raw);
                      const normalized = raw.replace(/,/g, '.');
                      if (normalized === '') {
                        onChange('price_cents', null);
                        return;
                      }
                      if (/^\d*\.?\d*$/.test(normalized)) {
                        const n = parseFloat(normalized);
                        if (!Number.isNaN(n)) {
                          onChange('price_cents', Math.round(n * 100));
                        }
                      }
                    }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">Leave empty for free programs</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Access Type</div>
                  <div className="inline-flex rounded-lg border border-border bg-card p-1">
                    <button
                      type="button"
                      onClick={() => {
                        onChange('is_group', false);
                        onChange('max_participants', '');
                      }}
                      className={`px-3 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] ${
                        !form.is_group ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-[var(--succession-hover-bg)]'
                      }`}
                    >
                      1:1
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange('is_group', true)}
                      className={`px-3 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] ${
                        form.is_group ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-[var(--succession-hover-bg)]'
                      }`}
                    >
                      Group
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Group allows multiple participants per cohort. 1:1 is single participant.
                  </p>
                </div>

                {form.is_group && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max Participants per Cohort</label>
                    <input
                      type="number"
                      min={1}
                      className="w-48 rounded-lg border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                      placeholder="e.g., 10"
                      value={form.max_participants}
                      onChange={(e) => onChange('max_participants', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resources */}
          {tab === 'resources' && !isNew && (
            <div className="rounded-2xl border border-border bg-card p-6">
              <ProgramResourcesUpload
                program={data?.sprintProgram}
                enrollmentCount={data?.sprintProgram?.enrollment_count || 0}
                onRefetch={() => {
                  // Refetch program query
                  window.location.reload();
                }}
              />
            </div>
          )}

          {tab === 'resources' && isNew && (
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-sm text-muted-foreground">
                Save your program first to upload resources.
              </p>
            </div>
          )}
        </div>

        {/* Live Preview */}
        {showPreview && (
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-border bg-card p-6 sticky top-6">
              <h3 className="font-semibold mb-4">Live Preview</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-bold">{form.title || 'Untitled Program'}</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {form.summary || 'No summary yet'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full border border-border bg-card px-2 py-0.5 text-xs">
                    {form.duration_days} days
                  </span>
                  <span className="rounded-full border border-border bg-card px-2 py-0.5 text-xs">
                    {form.level}
                  </span>
                  {form.goals.map((g) => (
                    <span key={g} className="rounded-full border border-border bg-card px-2 py-0.5 text-xs">
                      {g}
                    </span>
                  ))}
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Key Benefits:</p>
                  <ul className="space-y-1">
                    {form.benefits
                      .filter((b) => (b || '').trim())
                      .map((b, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex gap-2">
                          <span>•</span>
                          <span>{b}</span>
                        </li>
                      ))}
                  </ul>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Curriculum:</p>
                  <CurriculumPreview templates={templates} compact showDayOffset={false} />
                </div>

                <div className="pt-4 border-t">
                  <p className="text-lg font-bold">{priceDisplay}</p>
                  {form.is_group && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Group program • Max {form.max_participants || '∞'} participants
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </SingleColumnTemplate>
  );
}


